'use client';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@ui/button';
import { Heading, PillText } from '@web/atoms';
import { AddonSections, AddOnQuickView } from '@web/components';
import { SHOPIFY_TO_BC_SKU_MAP } from '../../constants';
import { buildElevarAddonProduct } from '../OnboardingFlowContainer/helpers';
import { CheckoutBar } from '../CheckoutBar';
import { useAddonModal } from '../../hooks';
import { CMSBlok } from '../../cms';
import { useReporter, useElevarReporter } from '../../reporter';
import {
  userExpandsAddonDetailsEvent,
  userUpdatesAddonsEvent,
  userViewsAddOnForm,
} from './events';
import { NOT_SELECTABLE_TEXT } from './constants';
import {
  SubtitleHeading,
  AddOnForm,
  BannerContainer,
  ExtrasSelectionContainer,
  ButtonContainer,
} from './styles';

export const ExtrasSelectionForm = ({
  headline,
  subheadline,
  onSubmit,
  onSkipButtonClick,
  addonSections,
  defaultAddons,
  unavailableAddons,
  modals,
  submitButtonText,
  onBackButtonClick,
  ctaButtonVariant = 'yellow',
  centerSkip = false,
  treatsForLife = false,
  pillText,
  pillVariant = 'dark',
  body,
}) => {
  const viewPageTagFired = useRef(false);
  const reporter = useReporter();
  const elevarReporter = useElevarReporter();
  const [selectedAddons, setSelectedAddons] = useState(
    defaultAddons.reduce(
      (cur, addon) => ({
        ...cur,
        [SHOPIFY_TO_BC_SKU_MAP[addon.sku] || addon.sku]: {
          sku: SHOPIFY_TO_BC_SKU_MAP[addon.sku] || addon.sku,
          quantity: addon.quantity,
          frequency: addon.frequency,
          recurrence: addon.recurrence,
          productName: addon.productName,
          productType: addon.productType,
          price: addon.price,
          discountedPrice: addon.discountedPrice,
          checked: !unavailableAddons.includes(addon.sku),
        },
      }),
      {},
    ),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { modal, onAddonDetailsClick } = useAddonModal(modals);

  const addons = addonSections.flatMap(section => section.addons);

  const availableAddons = addons.filter(
    ({ sku }) => !unavailableAddons.includes(sku),
  );

  const handleChange = newCardValues => {
    const { sku } = newCardValues;

    const prevCardValues = selectedAddons[sku];
    const newSelectedAddons = {
      ...selectedAddons,
      [sku]: { ...prevCardValues, ...newCardValues },
    };

    const wasChecked = !!prevCardValues?.checked;
    const isChecked = !!newSelectedAddons[sku]?.checked;
    const product = buildElevarAddonProduct(
      sku,
      addons,
      selectedAddons,
      SHOPIFY_TO_BC_SKU_MAP,
    );

    if (product?.variant_id) {
      if (!wasChecked && isChecked) {
        elevarReporter?.elevarAddToCart?.({
          product,
          userProperties: {},
          quantity: String(newSelectedAddons[sku]?.quantity || 1),
        });
      }
      if (wasChecked && !isChecked) {
        elevarReporter?.elevarRemoveFromCart?.({
          product,
          userProperties: {},
          quantity: String(prevCardValues?.quantity || 1),
        });
      }
    }

    setSelectedAddons(newSelectedAddons);
  };

  const onDetailsClickWithReporter = ({ value }) => {
    reporter.tag(userExpandsAddonDetailsEvent({ value }));

    const product = buildElevarAddonProduct(
      value,
      addons,
      selectedAddons,
      SHOPIFY_TO_BC_SKU_MAP,
    );
    if (product?.variant_id) {
      elevarReporter?.elevarViewItem({
        planType: product,
        userProperties: {},
      });
    }

    onAddonDetailsClick(value);
  };

  const checkedAddons = Object.keys(selectedAddons)
    .filter(sku => selectedAddons[sku].checked)
    .map(sku => selectedAddons[sku]);

  const onSubmitWithReporter = async event => {
    event.preventDefault();
    reporter.tag(userUpdatesAddonsEvent(availableAddons, checkedAddons));
    setIsSubmitting(true);
    await onSubmit(checkedAddons);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!viewPageTagFired.current) {
      reporter.tag(
        userViewsAddOnForm({
          availableAddons: availableAddons?.map(({ sku }) => sku),
          unavailableAddons,
        }),
      );
      viewPageTagFired.current = true;
    }
  }, [reporter, availableAddons, unavailableAddons]);

  const numAddonsSelected = checkedAddons.length;

  const checkoutBarProps = {
    ctaText: submitButtonText,
    shown: true,
    onSkipButtonClick: onSkipButtonClick,
    isSubmitDisabled:
      isSubmitting ||
      (defaultAddons.length === 0 && checkedAddons.length === 0),
    isBackButtonDisabled: isSubmitting,
    subtext: (
      <>
        <strong>{numAddonsSelected}</strong> extra{numAddonsSelected > 1 && 's'}{' '}
        in your subscription
      </>
    ),
    isSubmitting: isSubmitting,
    buttonVariant: ctaButtonVariant,
    skipButtonVariant: 'button',
  };

  return (
    <ExtrasSelectionContainer>
      <ButtonContainer aria-live="polite">
        <IconButton
          aria-label="go to previous step"
          icon={{
            iconName: 'arrow-left',
            size: '24px',
            alt: 'back',
            id: 'prev_step_footer',
            color: 'hero.plum',
          }}
          onClick={onBackButtonClick}
          type="button"
          variant="lilac"
        />
      </ButtonContainer>
      <AddOnForm onSubmit={onSubmitWithReporter}>
        {(treatsForLife || pillText) && (
          <BannerContainer>
            <PillText text={pillText} variant={pillVariant} size="large" />
          </BannerContainer>
        )}
        <Heading
          headingLevel="h3"
          typography={{ desktop: 'heading3', mobile: 'heading3' }}
          headingText={headline}
          position="center"
          verticalPadding={1}
          backgroundColor="background.base"
        />
        {subheadline && <SubtitleHeading>{subheadline}</SubtitleHeading>}
        <AddonSections
          addonSections={addonSections}
          addonSelectionState={selectedAddons}
          unavailableAddons={unavailableAddons}
          checkedAddons={checkedAddons}
          notSelectableText={NOT_SELECTABLE_TEXT}
          onChange={handleChange}
          onDetailsClick={onDetailsClickWithReporter}
          hideSectionHeading={true}
        />
        <CheckoutBar {...checkoutBarProps} centerSkip={centerSkip} />
      </AddOnForm>
      <AddOnQuickView
        {...modal}
        onSubmitAddonAddition={handleChange}
        checkedAddons={checkedAddons}
        addonSelectionState={selectedAddons}
        defaultValues={
          selectedAddons[modal?.sku] ?? {
            quantity: 1,
            frequency: 1,
            recurrence: null,
          }
        }
      />
      {body?.map(content => (
        <CMSBlok key={content._uid} content={content} />
      ))}
    </ExtrasSelectionContainer>
  );
};

ExtrasSelectionForm.displayName = 'ExtrasSelectionForm';
ExtrasSelectionForm.propTypes = {
  headline: PropTypes.string.isRequired,
  defaultAddons: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  subheadline: PropTypes.node.isRequired,
  addonSections: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      addons: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  unavailableAddons: PropTypes.array,
  modals: PropTypes.array.isRequired,
  submitButtonText: PropTypes.string.isRequired,
  onBackButtonClick: PropTypes.func.isRequired,
  onSkipButtonClick: PropTypes.func,
  ctaButtonVariant: PropTypes.string,
  centerSkip: PropTypes.bool,
  treatsForLife: PropTypes.bool,
  pillText: PropTypes.string,
  pillVariant: PropTypes.string,
  body: PropTypes.array,
};
